/* Pure directory normalization and matching, shared with the local checks. */
(() => {
  'use strict';
  const labels = { decks: 'Decks', stairs: 'Stairs', balconies: 'Balconies', 'pool-fencing': 'Pool fencing', commercial: 'Commercial' };
  const clean = value => typeof value === 'string' ? value.trim() : '';
  const normalize = value => clean(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  function safeURL(value) {
    if (!clean(value)) return '';
    try {
      const url = new URL(value, 'https://glass-railings.com');
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return '';
      return url.href;
    } catch { return ''; }
  }
  function postalCode(value) {
    const match = clean(value).match(/^(\d{5})(?:-\d{4})?$/);
    return match ? match[1] : '';
  }
  function coordinates(latitude, longitude) {
    if (latitude == null || longitude == null || latitude === '' || longitude === '') return null;
    const lat = Number(latitude), lng = Number(longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
  }
  function milesBetween(a, b) {
    const radians = Math.PI / 180;
    const lat = (b.lat - a.lat) * radians;
    const lng = (b.lng - a.lng) * radians;
    const h = Math.sin(lat / 2) ** 2 + Math.cos(a.lat * radians) * Math.cos(b.lat * radians) * Math.sin(lng / 2) ** 2;
    return 3958.7613 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  function specialties(values) {
    const result = new Set();
    (Array.isArray(values) ? values : []).forEach(value => {
      const text = normalize(value);
      if (/\bdecks?\b/.test(text)) result.add('decks');
      if (/\bstair(?:s|case|cases)?\b/.test(text)) result.add('stairs');
      if (/\bbalcon(?:y|ies)\b/.test(text)) result.add('balconies');
      if (/\bpool\b/.test(text) && /\bfenc(?:e|es|ing)\b/.test(text)) result.add('pool-fencing');
      if (/\bcommercial\b/.test(text)) result.add('commercial');
    });
    return [...result];
  }
  function normalizeProfiles(records, states, overrides = []) {
    const stateLookup = Object.fromEntries(states.flatMap(state => [[normalize(state.name), state.code], [state.code.toLowerCase(), state.code]]));
    const getState = value => stateLookup[normalize(value)] || '';
    const valid = (Array.isArray(records) ? records : []).filter(item => item && ['approved', 'applicant'].includes(item.status));
    valid.sort((a, b) => Number(b.status === 'approved') - Number(a.status === 'approved'));
    const seen = new Set();
    return valid.map((record, index) => {
      const areas = (Array.isArray(record.service_areas) ? record.service_areas : []).filter(area => area && typeof area === 'object').map(area => ({ label: clean(area.label) || clean(area.city), kind: clean(area.kind), city: clean(area.city), state: getState(area.state), zip: postalCode(area.postal_code) })).filter(area => area.state);
      const city = clean(record.city) || areas[0]?.city || '';
      const state = getState(record.state) || areas[0]?.state || '';
      const hasBusinessLocation = Boolean(clean(record.city) && getState(record.state));
      const name = clean(record.business_name);
      const slug = clean(record.slug) || normalize(name + ' ' + city + ' ' + state).replace(/ /g, '-');
      const key = normalize(name).replace(/\b(inc|llc|ltd|incorporated)\b/g, '').trim() + '|' + normalize(city) + '|' + state;
      if (!name || !state || seen.has(key) || seen.has(slug)) return null;
      seen.add(key); seen.add(slug);
      const override = overrides.find(item => clean(item.slug) === slug) || {};
      const capabilities = (Array.isArray(record.capabilities) ? record.capabilities : []).map(clean).filter(Boolean);
      const overrideSpecialties = clean(override.specialties).split(',').map(clean).filter(Boolean);
      const specialtyList = specialties(overrideSpecialties.length ? overrideSpecialties : [...capabilities, ...(record.specialties || [])]);
      const image = safeURL(override.image || record.profile_image);
      const stateNames = [...new Set([state, ...areas.map(area => area.state)])].map(code => states.find(item => item.code === code)?.name || code);
      const pageUrl = '/pages/installer-' + encodeURIComponent(slug);
      const hasProfilePage = record.status === 'applicant' || clean(record.profile_url) === pageUrl;
      return {
        id: slug || String(index), slug, name, city, state, stateNames, areas, hasBusinessLocation,
        profileUrl: hasProfilePage ? pageUrl : '#installer-' + encodeURIComponent(slug),
        openInDialog: !hasProfilePage,
        zip: postalCode(record.postal_code), coordinates: coordinates(record.latitude, record.longitude),
        status: record.status, description: clean(record.description), trade: clean(record.trade),
        website: safeURL(record.website), phone: record.status === 'approved' ? clean(record.public_phone) : '',
        email: record.status === 'approved' ? clean(record.public_email) : '',
        specialties: specialtyList, capabilities, specialtySource: safeURL(record.specialty_source),
        areaSource: safeURL(record.service_area_source), areaNote: clean(record.service_area_note),
        image, imageAlt: clean(override.image_alt) || 'Project by ' + name,
        gallery: safeURL(override.gallery_url || record.gallery_url),
        searchText: normalize([name, city, state, ...stateNames, ...areas.flatMap(area => [area.label, area.city, area.state]), record.trade].join(' '))
      };
    }).filter(Boolean);
  }
  function buildPostalLookup(index) {
    const cityPoints = new Map();
    for (const value of Object.values(index)) {
      if (!Array.isArray(value)) continue;
      const point = coordinates(value[2], value[3]);
      if (!point) continue;
      const key = normalize(value[0]) + '|' + value[1];
      const total = cityPoints.get(key) || { lat: 0, lng: 0, count: 0 };
      total.lat += point.lat; total.lng += point.lng; total.count++;
      cityPoints.set(key, total);
    }
    return {
      zip(value) {
        const row = index[postalCode(value)];
        const point = row && coordinates(row[2], row[3]);
        return point ? { ...point, city: row[0], state: row[1], zip: postalCode(value) } : null;
      },
      city(city, state) {
        const total = cityPoints.get(normalize(city) + '|' + state);
        return total ? { lat: total.lat / total.count, lng: total.lng / total.count } : null;
      }
    };
  }
  function distanceToProfile(profile, origin, lookup) {
    if (!origin || !profile.hasBusinessLocation) return null;
    const points = [];
    if (profile.coordinates) points.push(profile.coordinates);
    else if (lookup) {
      const base = lookup.zip(profile.zip) || lookup.city(profile.city, profile.state);
      if (base && (!base.state || base.state === profile.state)) points.push(base);
    }
    // A service area is not another office. Distance always refers to the business location.
    return points.length ? Math.min(...points.map(point => milesBetween(origin, point))) : null;
  }
  function filterProfiles(profiles, filters, lookup) {
    const query = normalize(filters.query);
    const words = query.split(' ').filter(Boolean);
    return profiles.map(profile => ({ profile, distance: distanceToProfile(profile, filters.origin, lookup) })).filter(({ profile, distance }) => {
      if (filters.state && filters.state !== 'all' && profile.state !== filters.state && !profile.areas.some(area => area.state === filters.state)) return false;
      if (filters.specialty && filters.specialty !== 'all' && !profile.specialties.includes(filters.specialty)) return false;
      if (filters.origin && (distance == null || distance > filters.radius)) return false;
      return words.every(word => profile.searchText.includes(word));
    }).sort((a, b) => filters.origin
      ? a.distance - b.distance || a.profile.name.localeCompare(b.profile.name)
      : Number(b.profile.status === 'approved') - Number(a.profile.status === 'approved') || a.profile.name.localeCompare(b.profile.name));
  }
  const api = { labels, clean, normalize, safeURL, postalCode, coordinates, milesBetween, specialties, normalizeProfiles, buildPostalLookup, distanceToProfile, filterProfiles };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.InstallerFinderCore = api;
})();
